import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Mail, Lock, User, Eye, EyeOff, ArrowRight, Check, Loader2 } from 'lucide-react';
import { useAuthStore, register } from '../store/auth';
import { useThemeStore } from '../store/theme';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export default function Register() {
  const navigate = useNavigate();
  const { setUser, setError } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  // Scroll to top on page load/reload
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setLocalError] = useState('');

  // Password strength
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
  };

  const passwordStrength = Object.values(passwordChecks).filter(Boolean).length;

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    setLocalError('');

    try {
      const response = await fetch(`${API_URL}/auth/google`);
      const data = await response.json() as { success: boolean; data?: { url: string }; error?: string };

      if (data.success && data.data?.url) {
        window.location.href = data.data.url;
      } else {
        throw new Error(data.error || 'Failed to initiate Google sign-in');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Google sign-in failed';
      setLocalError(message);
      setIsGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (passwordStrength < 3) {
      setLocalError('Please use a stronger password');
      return;
    }

    setIsLoading(true);

    try {
      const data = await register(email, password, name);
      if (data.data) {
        setUser(data.data.user, data.data.token);
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setLocalError(message);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-accent flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>SweetPilot</span>
          </Link>

          {/* Header */}
          <div className="mb-8">
            <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Create your account</h1>
            <p className={isDark ? 'text-text-secondary' : 'text-slate-600'}>
              Start your free trial with 100 credits
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-accent-red/10 border border-accent-red/20 rounded-xl"
            >
              <p className="text-accent-red text-sm">{error}</p>
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                Full Name
              </label>
              <div className="relative">
                <User className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                  className={`w-full pl-12 pr-4 py-3 rounded-xl border transition-colors ${
                    isDark
                      ? 'bg-dark-800 border-dark-600 text-white placeholder-text-muted focus:border-accent-purple'
                      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'
                  }`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                Email
              </label>
              <div className="relative">
                <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className={`w-full pl-12 pr-4 py-3 rounded-xl border transition-colors ${
                    isDark
                      ? 'bg-dark-800 border-dark-600 text-white placeholder-text-muted focus:border-accent-purple'
                      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'
                  }`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                Password
              </label>
              <div className="relative">
                <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className={`w-full pl-12 pr-12 py-3 rounded-xl border transition-colors ${
                    isDark
                      ? 'bg-dark-800 border-dark-600 text-white placeholder-text-muted focus:border-accent-purple'
                      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Password Strength */}
              {password && (
                <div className="mt-3 space-y-2">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          passwordStrength >= level
                            ? level <= 2
                              ? 'bg-accent-red'
                              : level === 3
                              ? 'bg-accent-orange'
                              : 'bg-accent-green'
                            : isDark ? 'bg-dark-600' : 'bg-slate-200'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {[
                      { check: passwordChecks.length, label: '8+ characters' },
                      { check: passwordChecks.uppercase, label: 'Uppercase letter' },
                      { check: passwordChecks.lowercase, label: 'Lowercase letter' },
                      { check: passwordChecks.number, label: 'Number' },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className={`flex items-center ${
                          item.check ? 'text-accent-green' : isDark ? 'text-text-muted' : 'text-slate-400'
                        }`}
                      >
                        <Check className="w-3 h-3 mr-1" />
                        {item.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <span>Create Account</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            <p className={`text-xs text-center ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              By signing up, you agree to our{' '}
              <Link to="/terms" className="text-accent-purple hover:underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-accent-purple hover:underline">
                Privacy Policy
              </Link>
            </p>
          </form>

          {/* Sign In Link */}
          <p className={`mt-6 text-center text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-accent-purple hover:text-accent-purple-light font-medium"
            >
              Sign in
            </Link>
          </p>

          {/* Divider */}
          <div className="my-5 flex items-center">
            <div className={`flex-1 border-t ${isDark ? 'border-border' : 'border-slate-300'}`}></div>
            <span className={`px-4 text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>or continue with</span>
            <div className={`flex-1 border-t ${isDark ? 'border-border' : 'border-slate-300'}`}></div>
          </div>

          {/* Social Login - Google Only */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isGoogleLoading}
            className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-xl border transition-colors ${
              isDark
                ? 'bg-dark-800 border-dark-600 text-white hover:bg-dark-700'
                : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {isGoogleLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            <span>Google</span>
          </button>
        </motion.div>
      </div>

      {/* Right Side - Gradient Background */}
      <div className={`hidden lg:flex flex-1 items-center justify-center relative overflow-hidden ${isDark ? 'bg-dark-800' : 'bg-gradient-to-br from-cyan-50 to-purple-100'}`}>
        <div className="absolute inset-0">
          <div className={`absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl ${isDark ? 'bg-accent-cyan/30' : 'bg-cyan-300/40'}`} />
          <div className={`absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl ${isDark ? 'bg-accent-purple/30' : 'bg-purple-300/40'}`} />
        </div>

        <div className="relative z-10 max-w-md px-8">
          <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            What you'll get with SweetPilot:
          </h2>
          <ul className="space-y-4">
            {[
              'Access to 15 AI employees',
              '100 free credits to start',
              '100+ one-click task templates',
              'Brain AI for custom training',
              '24/7 automation capabilities',
              '100+ language support',
            ].map((item) => (
              <li key={item} className={`flex items-center ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                <div className="w-6 h-6 rounded-full bg-accent-green/20 flex items-center justify-center mr-3">
                  <Check className="w-4 h-4 text-accent-green" />
                </div>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
